// Takeoff Canvas 2.0 — Back-Out toolbar entry (2026-09-14). A back-out is a
// deduction drawn against ONE Room Element's area (D-2, takeoffExclusions.js),
// so entering the tool from the command bar needs an element picker: which
// element, in which room, does this back-out apply to? This lists the
// candidates — a floor- or ceiling-area element, quantity method
// 'calculated', no formula (a formula replaces the basis, so a back-out has
// nothing to deduct from), on a room that is placed on the canvas — with the
// selected rooms' elements first. The element card's own "+ Add Back-Out"
// stays the direct path when the estimator is already in the room.

import { BACK_OUT_BASES } from "./takeoffExclusions";
import { inferElementBasis } from "../config/roomElements";

const placed = (r) => r && r.CanvasX != null && r.CanvasX !== "" && r.CanvasY != null && r.CanvasY !== "";

// Why an element cannot take a back-out (null = eligible).
export function backOutIneligibility(element, room) {
  if (!room) return "room_missing";
  if (!placed(room)) return "room_unplaced";
  const basis = inferElementBasis(element);
  if (!BACK_OUT_BASES.has(basis)) return "basis_not_area";
  if (element.QuantityFormulaID != null) return "formula_replaces_basis";
  if ((element.QuantityMethod || "calculated") !== "calculated") return "method_not_calculated";
  return null;
}

// → [{ element, room, basis, selected }] — selected rooms first, then by the
// room's display order / name, then by element discipline + name.
export function backOutEligibleElements(elements = [], rooms = [], { selectedRoomIds = new Set() } = {}) {
  const byId = new Map(rooms.map(r => [r.RoomID, r]));
  const out = [];
  for (const el of elements) {
    const room = byId.get(el.RoomID);
    if (backOutIneligibility(el, room)) continue;
    out.push({ element: el, room, basis: inferElementBasis(el), selected: selectedRoomIds.has(el.RoomID) });
  }
  const roomOrder = (r) => [r.DisplayOrder ?? 1e9, String(r.RoomName || "")];
  out.sort((a, b) => {
    if (a.selected !== b.selected) return a.selected ? -1 : 1;
    const [ao, an] = roomOrder(a.room), [bo, bn] = roomOrder(b.room);
    if (ao !== bo) return ao - bo;
    if (an !== bn) return an.localeCompare(bn);
    return String(a.element.Name || a.element.Code || "").localeCompare(String(b.element.Name || b.element.Code || ""));
  });
  return out;
}

export const BACK_OUT_INELIGIBILITY_LABELS = Object.freeze({
  room_missing: "its room no longer exists",
  room_unplaced: "its room is not placed on the canvas",
  basis_not_area: "only floor- and ceiling-area elements take a back-out",
  formula_replaces_basis: "a formula supplies its quantity, so there is no area to deduct from",
  method_not_calculated: "its quantity is fixed / placed, not calculated from the room",
});
